/**
 * データ有効性・鮮度判定ユーティリティ
 */

// Firestore Timestamp duck-typing interface
export interface FirestoreTimestamp {
  toDate(): Date;
}

/**
 * センサーの最終更新日時が指定時間（デフォルト3時間）以内かを判定する
 * 
 * @param lastUpdatedAt 最終更新日時 (Date または FirestoreのTimestamp形式)。null/undefined の場合は false を返す。
 * @param referenceDate 基準日時 (省略時は現在日時)
 * @param maxHours 最大許容時間 (デフォルト3時間)
 * @returns maxHours以内であれば true
 */
export function isDataFresh(
  lastUpdatedAt?: Date | FirestoreTimestamp | null,
  referenceDate: Date = new Date(),
  maxHours: number = 3
): boolean {
  if (!lastUpdatedAt) return false;
  if (maxHours <= 0) return false;

  const lastUpdatedDate = 
    lastUpdatedAt instanceof Date ? lastUpdatedAt : lastUpdatedAt.toDate();

  const diffMs = referenceDate.getTime() - lastUpdatedDate.getTime();

  // 未来のタイムスタンプは異常値として扱う
  if (diffMs < 0) return false;

  const diffHours = diffMs / (1000 * 60 * 60);

  return diffHours <= maxHours;
}

/**
 * 日時文字列が基準日時から指定分数以内かを判定する
 */
export function isWithinMinutes(
  dateStr?: string | null,
  minutes: number = 10,
  referenceDate: Date = new Date()
): boolean {
  if (!dateStr) return false;
  if (minutes <= 0) return false;

  const targetDate = new Date(dateStr);
  const targetTime = targetDate.getTime();
  if (Number.isNaN(targetTime)) return false;

  const diffMs = referenceDate.getTime() - targetTime;
  if (diffMs < 0) return false;

  return diffMs <= minutes * 60 * 1000;
}

/**
 * アラート日時文字列が指定時間以内かを判定する
 */
export function isAlertDateValid(
  dateStr?: string | null,
  maxHours: number = 3,
  referenceDate: Date = new Date()
): boolean {
  if (!dateStr) return false;
  if (maxHours <= 0) return false;

  const targetDate = new Date(dateStr);
  const targetTime = targetDate.getTime();
  if (Number.isNaN(targetTime)) return false;

  return isDataFresh(targetDate, referenceDate, maxHours);
}

/**
 * 冬期（12月、1月、2月）かを判定する
 */
export function isWinterSeason(referenceDate: Date = new Date()): boolean {
  const month = referenceDate.getMonth();
  return month === 11 || month === 0 || month === 1;
}

/**
 * アラート表示用に日時文字列を短い形式へ整形する
 */
export function formatAlertDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;

  return new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * 指定時刻が稼働時間帯内かを判定する
 * 
 * @param date 判定対象の日付
 * @param startHour 稼働開始時間 (0-23)
 * @param endHour 稼働終了時間 (0-23)。排他的（この時刻は含まない）。
 * @returns 稼働時間帯内（startHour 以上 endHour 未満）であれば true (UTC 基準で判定する)
 */
export function isWithinOperatingHours(
  date: Date,
  startHour: number,
  endHour: number
): boolean {
  const currentHour = date.getUTCHours();
  
  if (startHour <= endHour) {
    // 例: 5:00 〜 19:00
    return currentHour >= startHour && currentHour < endHour;
  } else {
    // 例: 22:00 〜 5:00 (日またぎ)
    return currentHour >= startHour || currentHour < endHour;
  }
}
